import React, { SyntheticEvent, useEffect, useState } from "react";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { cloneDeep, get, merge } from "lodash";
import * as yup from "yup";

import {
  FEEDBACK_TYPE_FEEDBACK,
  FEEDBACK_TYPE_BUGS,
  FEEDBACK_TYPE_FEATURE_REQ,
} from "../../model";
import { UserLoginInfo } from "../../types/context";
import { UserContext } from "../../context/userContext";
import { useContext } from "react";
import { appLogger, Http } from "../../utils";
import { withSnackbar } from "../../utils/common/SnackbarHoc";
import EmailSettingsHeader from "./EmailSettingsHeader";
import {
  enableFeedback,
  enableBugReport,
  enableFeatureRequest,
  enableChatbot,
} from "../../utils/config";
import Feedback from "./Feedback";
import Ticket from "./Ticket";
import FeatureRequest from "./FeatureRequest";
import {
  IConnectionDetails,
  IFeatureRequestAISettings,
  IFeedbackAISettings,
  ITicketAISettings,
} from "./types";
import {
  defaultFeatureRequestAISettings,
  defaultFeatureRequestAccordionSettings,
  defaultFeedbackAISettings,
  defaultFeedbackAccordionSettings,
  defaultTicketAISettings,
  defaultTicketAccordionSettings,
  resetFeedbackAccordionSettings,
  resetTicketAccordionSettings,
  resetFeatureRequestAccordionSettings,
  defaultConnectionDetails,
  defaultIsAutoAssign,
  defaultTicketEmailSettings,
  defaultFeedbackEmailSettings,
  defaultFeatureRequestEmailSettings,
} from "./constants";

import "./index.css";
import { validateOpenAIAPIKey } from "../../utils/common/common";

interface EmailSettingsProps {
  snackbarShowMessage: any;
}

const EmailSettings = ({ snackbarShowMessage }: EmailSettingsProps) => {
  const { productId, versionId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [activeTabLabel, setActiveTabLabel] = useState("Feedback");
  const [product, setProduct] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [bugSeverities, setBugSeverities] = useState<string[]>([]);
  const [kbArticles, setKBArticles] = useState([]);
  const [feedbackAccordionSettings, setFeedbackAccordionSettings] = useState(
    defaultFeedbackAccordionSettings
  );
  const [ticketAccordionSettings, setTicketAccordionSettings] = useState(
    defaultTicketAccordionSettings
  );
  const [featureRequestAccordionSettings, setFeatureRequestAccordionSettings] =
    useState(defaultFeatureRequestAccordionSettings);
  const [feedbackAISettings, setFeedbackAISettings] =
    useState<IFeedbackAISettings>(defaultFeedbackAISettings);
  const [ticketAISettings, setTicketAISettings] = useState<ITicketAISettings>(
    defaultTicketAISettings
  );
  const [featureRequestAISettings, setFeatureRequestAISettings] =
    useState<IFeatureRequestAISettings>(defaultFeatureRequestAISettings);
  const [feedbackConnectionDetails, setFeedbackConnectionDetails] =
    useState<IConnectionDetails>(defaultConnectionDetails);
  const [ticketConnectionDetails, setTicketConnectionDetails] =
    useState<IConnectionDetails>(defaultConnectionDetails);
  const [featureRequestConnectionDetails, setFeatureRequestConnectionDetails] =
    useState<IConnectionDetails>(defaultConnectionDetails);
  const [isAutoAssign, setIsAutoAssign] = useState(defaultIsAutoAssign);
  const [feedbackEmailSettings, setFeedbackEmailSettings] = useState(
    defaultFeedbackEmailSettings
  );
  const [ticketEmailSettings, setTicketEmailSettings] = useState(
    defaultTicketEmailSettings
  );
  const [featureRequestEmailSettings, setFeatureRequestEmailSettings] =
    useState(defaultFeatureRequestEmailSettings);

  const { userInfo } = useContext(UserContext) as UserLoginInfo;

  let userEnabledFeatures: any = localStorage.getItem("userenabledFeatures");
  if (userEnabledFeatures) {
    userEnabledFeatures = JSON.parse(userEnabledFeatures);
  }

  const [isFeedbackEnabled, setIsFeedbackEnabled] = useState(false);
  const [isTicketEnabled, setIsTicketEnabled] = useState(false);
  const [isFeatureRequestEnabled, setIsFeatureRequestEnabled] = useState(false);

  const handleTabChange = (event: any, newValue: number) => {
    const tabLabel = event.target.innerText;
    setActiveTab(newValue);
    setActiveTabLabel(tabLabel);
  };

  const handleIsAutoAssignChange = (event: any, type: any) => {
    setIsAutoAssign((prevState) => ({
      ...prevState,
      [type]: event.target.checked,
    }));
  };

  const isJSONString = (value: any) => {
    try {
      JSON.parse(value);
      return true;
    } catch (error) {
      return false;
    }
  };

  const getConnectionDetailsSchema = (type: string) => {
    return yup.object().shape({
      useSecureConnection: yup.boolean(),
      emailProvider: yup
        .string()
        .required(`Email provider is required (${type} : Connection Details)`),
      host: yup.string().when("authenticationMode", {
        is: (authenticationMode: string) =>
          ["OAuth2", "basic"].includes(authenticationMode),
        then: yup
          .string()
          .required(`Host is required (${type} : Connection Details)`),
        otherwise: yup.string(),
      }),
      port: yup.number().when("authenticationMode", {
        is: (authenticationMode: string) =>
          ["OAuth2", "basic"].includes(authenticationMode),
        then: yup
          .number()
          .typeError(`Port is required (${type} : Connection Details)`)
          .required(`Port is required (${type} : Connection Details)`),
        otherwise: yup
          .number()
          .nullable()
          .transform((_, val) => (val !== "" ? Number(val) : null)),
      }),
      authenticationMode: yup
        .string()
        .required(
          `Authentication mode is required (${type} : Connection Details)`
        ),
      username: yup.string().when("authenticationMode", {
        is: "basic",
        then: yup
          .string()
          .required(`Username is required (${type} : Connection Details)`),
        otherwise: yup.string(),
      }),
      password: yup.string().when("authenticationMode", {
        is: "basic",
        then: yup
          .string()
          .required(`Password is required (${type} : Connection Details)`),
        otherwise: yup.string(),
      }),
      clientID: yup.string().when("authenticationMode", {
        is: "OAuth2",
        then: yup
          .string()
          .required(`Client ID is required (${type} : Connection Details)`),
        otherwise: yup.string(),
      }),
      tenantID: yup.string().when("authenticationMode", {
        is: "OAuth2",
        then: yup
          .string()
          .required(`Tenant ID is required (${type} : Connection Details)`),
        otherwise: yup.string(),
      }),
      OAuthUsername: yup.string().when("authenticationMode", {
        is: "OAuth2",
        then: yup
          .string()
          .required(`Username is required (${type} : Connection Details)`),
        otherwise: yup.string(),
      }),
      clientSecret: yup.string().when("authenticationMode", {
        is: "OAuth2",
        then: yup
          .string()
          .required(`Client secret is required (${type} : Connection Details)`),
        otherwise: yup.string(),
      }),
      mailServiceAccount: yup.string().when("authenticationMode", {
        is: "oauth2ServiceAccount",
        then: yup
          .string()
          .required(
            `Mail service account is required (${type} : Connection Details)`
          )
          .test(
            "is-json",
            `Mail service account data must be JSON type (${type} : Connection Details)`,
            isJSONString
          ),
        otherwise: yup.string(),
      }),
      emailAddress: yup
        .string()
        .required(`Email address is required (${type} : Connection Details)`)
        .email(`Invalid email address (${type} : Connection Details)`),
    });
  };

  const getIntelligenceLayerSchema = (type: string) => {
    return yup.object().shape({
      question: yup
        .string()
        .required(`Question is required (${type} : Pre AI Settings)`),
      answer: yup.string().when("question", {
        is: "",
        otherwise: yup
          .string()
          .required(`Answer is required (${type} : Pre AI Settings)`),
        then: yup.string(),
      }),
    });
  };

  const getAISettingsSchema = (type: string) => {
    return yup.object().shape({
      llmArchitecture: yup
        .string()
        .required(`Model architecture is required (${type} : AI Settings)`),
      tone: yup
        .string()
        .required(`Bot tone is required (${type} : AI Settings)`),
      lengthOfResponse: yup
        .number()
        .typeError(`Length of bot response is required (${type} : AI Settings)`)
        .required(`Length of bot response is required (${type} : AI Settings)`)
        .min(
          10,
          `Minimum length of bot response should be 10 (${type} : AI Settings)`
        ),
      intelligenceLayer: yup.array().of(getIntelligenceLayerSchema(type)),
      aiCreativity: yup
        .number()
        .required(`Bot's creativity is required (${type} : AI Settings)`),
      openaiKey: yup
        .string()
        .required(`ChatGPT API key is required (${type} : AI Settings)`),
    });
  };

  const getFeedbackEmailSettingsSchema = () => {
    return yup.object().shape({
      sendAutoReply: yup.boolean(),
      autoReplyRatings: yup.object().when("sendAutoReply", {
        is: true,
        then: yup.object().test({
          name: "at-least-one-key-value-pair",
          message:
            "Atleast one rating should be selected (Feedback : Email Settings)",
          test: (obj) => Object.keys(obj).length > 0,
        }),
        otherwise: yup.object(),
      }),
      autoReplyEmailSubject: yup.string().when("sendAutoReply", {
        is: true,
        then: yup
          .string()
          .required(`Email subject is required (Feedback : Email Settings)`),
        otherwise: yup.string(),
      }),
    });
  };

  const getBugsEmailSettingsSchema = () => {
    return yup.object().shape({
      sendAutoReply: yup.boolean(),
      autoReplySeverities: yup.object().when("sendAutoReply", {
        is: true,
        then: yup.object().test({
          name: "at-least-one-key-value-pair",
          message:
            "Atleast one severity should be selected (Ticket : Email Settings)",
          test: (obj) => Object.keys(obj).length > 0,
        }),
        otherwise: yup.object(),
      }),
      autoReplyEmailSubject: yup.string().when("sendAutoReply", {
        is: true,
        then: yup
          .string()
          .required(`Email subject is required (Ticket : Email Settings)`),
        otherwise: yup.string(),
      }),
    });
  };

  const getFeatureRequestEmailSettingsSchema = () => {
    return yup.object().shape({
      sendAutoReply: yup.boolean(),
      autoReplyEmailSubject: yup.string().when("sendAutoReply", {
        is: true,
        then: yup
          .string()
          .required(
            `Email subject is required (Feature Request : Email Settings)`
          ),
        otherwise: yup.string(),
      }),
    });
  };

  const applicableSchemas: any = {};
  if (isFeedbackEnabled) {
    applicableSchemas["FEEDBACK"] = yup.object().shape({
      aiSettings: getAISettingsSchema("Feedback"),
      emailSettings: getFeedbackEmailSettingsSchema(),
      connectionDetails: getConnectionDetailsSchema("Feedback"),
    });
  }
  if (isTicketEnabled) {
    applicableSchemas["BUGS"] = yup.object().shape({
      aiSettings: getAISettingsSchema("Ticket"),
      emailSettings: getBugsEmailSettingsSchema(),
      connectionDetails: getConnectionDetailsSchema("Ticket"),
    });
  }
  if (isFeatureRequestEnabled) {
    applicableSchemas["FEATURE_REQUEST"] = yup.object().shape({
      aiSettings: getAISettingsSchema("Feature Request"),
      emailSettings: getFeatureRequestEmailSettingsSchema(),
      connectionDetails: getConnectionDetailsSchema("Feature Request"),
    });
  }

  const validationSchema = yup.object().shape({
    ...applicableSchemas,
  });

  const isDataValid = async (emailConfig: any) => {
    if (!product) return false;
    try {
      // Validate OpenAI API key
      if (
        emailConfig &&
        emailConfig[FEEDBACK_TYPE_FEEDBACK] &&
        emailConfig[FEEDBACK_TYPE_FEEDBACK].aiSettings &&
        emailConfig[FEEDBACK_TYPE_FEEDBACK].aiSettings.openaiKey
      ) {
        const isAPIKeyValid = await validateOpenAIAPIKey(
          emailConfig[FEEDBACK_TYPE_FEEDBACK].aiSettings.openaiKey
        );
        if (!isAPIKeyValid) {
          snackbarShowMessage(
            "Please provide a valid ChatGPT API key under the AI Settings",
            "error",
            null
          );
          setLoading(false);
          return;
        }
      } else if (
        emailConfig &&
        emailConfig[FEEDBACK_TYPE_BUGS] &&
        emailConfig[FEEDBACK_TYPE_BUGS].aiSettings &&
        emailConfig[FEEDBACK_TYPE_BUGS].aiSettings.openaiKey
      ) {
        const isAPIKeyValid = await validateOpenAIAPIKey(
          emailConfig[FEEDBACK_TYPE_BUGS].aiSettings.openaiKey
        );
        if (!isAPIKeyValid) {
          snackbarShowMessage(
            "Please provide a valid ChatGPT API key under the AI Settings",
            "error",
            null
          );
          setLoading(false);
          return;
        }
      } else if (
        emailConfig &&
        emailConfig[FEEDBACK_TYPE_FEATURE_REQ] &&
        emailConfig[FEEDBACK_TYPE_FEATURE_REQ].aiSettings &&
        emailConfig[FEEDBACK_TYPE_FEATURE_REQ].aiSettings.openaiKey
      ) {
        const isAPIKeyValid = await validateOpenAIAPIKey(
          emailConfig[FEEDBACK_TYPE_FEATURE_REQ].aiSettings.openaiKey
        );
        if (!isAPIKeyValid) {
          snackbarShowMessage(
            "Please provide a valid ChatGPT API key under the AI Settings",
            "error",
            null
          );
          setLoading(false);
          return;
        }
      }
      await validationSchema.validate(emailConfig);
      return true;
    } catch (validationError: any) {
      snackbarShowMessage(validationError.message, "error");
      return false;
    }
  };

  const handleSave = async () => {
    let emailConfig = cloneDeep(product.emailConfig);
    emailConfig = merge(emailConfig, {
      FEEDBACK: {
        aiSettings: feedbackAISettings,
        connectionDetails: feedbackConnectionDetails,
        emailSettings: feedbackEmailSettings,
      },
      BUGS: {
        aiSettings: ticketAISettings,
        connectionDetails: ticketConnectionDetails,
        emailSettings: ticketEmailSettings,
      },
      FEATURE_REQUEST: {
        aiSettings: featureRequestAISettings,
        connectionDetails: featureRequestConnectionDetails,
        emailSettings: featureRequestAISettings,
      },
    });

    const isFormValid = await isDataValid(emailConfig);
    if (!isFormValid) return;

    setLoading(true);
    const updatedProduct = cloneDeep(product);
    updatedProduct.emailConfig = emailConfig;

    const autoAssignConfig = cloneDeep(updatedProduct.autoAssignConfig);
    const updatedAutoAssignConfig = {
      FEEDBACK: {
        isAutoAssign: isAutoAssign[FEEDBACK_TYPE_FEEDBACK],
        inputSource: [],
      },
      BUGS: {
        isAutoAssign: isAutoAssign[FEEDBACK_TYPE_BUGS],
        inputSource: [],
      },
      FEATURE_REQUEST: {
        isAutoAssign: isAutoAssign[FEEDBACK_TYPE_FEATURE_REQ],
        inputSource: [],
      },
    };
    updatedProduct.autoAssignConfig = merge(
      autoAssignConfig,
      updatedAutoAssignConfig
    );

    Http.put({
      url: `/api/v2/products`,
      body: {
        products: [updatedProduct],
      },
      state: userInfo,
    })
      .then(() => {
        snackbarShowMessage(
          "Email configurations saved successfully",
          "success",
          3000
        );
      })
      .catch((error: any) => {
        appLogger.error({
          errObj: {
            method: "putUpdate",
            component: "email-settings",
            fileName: "index.tsx",
            err: error,
          },
        });
        snackbarShowMessage(error.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleFeedbackAccordionChange = (name: string) => {
    setFeedbackAccordionSettings((prevState) => ({
      ...resetFeedbackAccordionSettings,
      [name]: !prevState[name as keyof typeof feedbackAccordionSettings],
    }));
  };

  const handleTicketAccordionChange = (name: string) => {
    setTicketAccordionSettings((prevState) => ({
      ...resetTicketAccordionSettings,
      [name]: !prevState[name as keyof typeof ticketAccordionSettings],
    }));
  };

  const handleFeatureRequestAccordionChange = (name: string) => {
    setFeatureRequestAccordionSettings((prevState) => ({
      ...resetFeatureRequestAccordionSettings,
      [name]: !prevState[name as keyof typeof featureRequestAccordionSettings],
    }));
  };

  const setFormData = (fetchedProduct: any): void => {
    const emailConfig = fetchedProduct.emailConfig;
    if (!emailConfig) return;

    const { FEEDBACK, BUGS, FEATURE_REQUEST } = emailConfig;
    if (FEEDBACK) {
      setFeedbackAISettings(FEEDBACK.aiSettings);
      setFeedbackConnectionDetails(FEEDBACK.connectionDetails);
      setFeedbackEmailSettings(FEEDBACK.emailSettings);
    }
    if (BUGS) {
      setTicketAISettings(BUGS.aiSettings);
      setTicketConnectionDetails(BUGS.connectionDetails);
      setTicketEmailSettings(BUGS.emailSettings);
    }
    if (FEATURE_REQUEST) {
      setFeatureRequestAISettings(FEATURE_REQUEST.aiSettings);
      setFeatureRequestConnectionDetails(FEATURE_REQUEST.connectionDetails);
      setFeatureRequestEmailSettings(FEATURE_REQUEST.emailSettings);
    }

    const autoAssignConfig = fetchedProduct.autoAssignConfig;
    if (!autoAssignConfig) return;

    setIsAutoAssign(() => {
      return {
        FEEDBACK: get(autoAssignConfig.FEEDBACK, "isAutoAssign", false),
        BUGS: get(autoAssignConfig.BUGS, "isAutoAssign", false),
        FEATURE_REQ: get(
          autoAssignConfig.FEATURE_REQUEST,
          "isAutoAssign",
          false
        ),
      };
    });
  };

  const setTabs = (fetchedProduct: any) => {
    const feedbackTypes = fetchedProduct?.feedbackAgentSettings?.feedbackTypes;
    const feedbackEnabled =
      userEnabledFeatures?.enableFeedback &&
      enableFeedback &&
      fetchedProduct &&
      feedbackTypes &&
      feedbackTypes.includes(FEEDBACK_TYPE_FEEDBACK);
    const bugReportEnabled =
      userEnabledFeatures?.enableBugReport && enableBugReport;
    const chatbotEnabled = userEnabledFeatures?.enableChatbot && enableChatbot;
    const ticketEnabled =
      (bugReportEnabled || chatbotEnabled) &&
      fetchedProduct &&
      feedbackTypes &&
      feedbackTypes.includes(FEEDBACK_TYPE_BUGS);
    const featureRequestEnabled =
      userEnabledFeatures?.enableFeatureRequest &&
      enableFeatureRequest &&
      fetchedProduct &&
      feedbackTypes &&
      feedbackTypes.includes(FEEDBACK_TYPE_FEATURE_REQ);

    setIsFeedbackEnabled(feedbackEnabled);
    setIsTicketEnabled(ticketEnabled);
    setIsFeatureRequestEnabled(featureRequestEnabled);

    if (!feedbackEnabled && ticketEnabled) {
      setActiveTabLabel("Ticket");
    } else if (!feedbackEnabled && !ticketEnabled && featureRequestEnabled) {
      setActiveTabLabel("Feature Request");
    }
  };

  useEffect(() => {
    if (!productId || !versionId) return;

    setLoading(true);
    const fetchData = async () => {
      try {
        // Fetch product data
        const productData: any = await Http.get({
          url: `/api/v2/products/${productId}/${versionId}`,
          state: userInfo,
        });
        const fetchedProduct = productData?.products?.[0];
        if (!fetchedProduct) {
          snackbarShowMessage("Error fetching product!", "error", 3000);
          return;
        }

        setProduct(fetchedProduct);
        setFormData(fetchedProduct);
        setBugSeverities(() => {
          const defaultBugSeverities = ["Any"];
          const productBugSeverities =
            fetchedProduct.feedbackAgentSettings?.bugSettings?.severities;

          if (!productBugSeverities) return defaultBugSeverities;

          defaultBugSeverities.push(...productBugSeverities);
          return defaultBugSeverities;
        });
        setTabs(fetchedProduct);

        // Fetch KB articles
        const kbArticleData: any = await Http.get({
          url: `/api/v2/kbArticles/${productId}/${versionId}`,
        });
        if (!kbArticleData?.kbArticles) {
          snackbarShowMessage("Error fetching knowledge base!", "error", 3000);
          return;
        }

        setKBArticles(() => {
          const kbFolders = kbArticleData.kbArticles.filter(
            (article: any) => article.type === "folder"
          );

          return kbFolders.map((e: any) => ({ name: e.name, id: e.id }));
        });
      } catch (error) {
        appLogger.error({
          errObj: {
            method: "useEffect",
            component: "email-settings",
            fileName: "index.tsx",
            err: error,
          },
        });

        snackbarShowMessage("Error fetching product!", "error", 3000);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [productId, versionId]);

  return (
    <div
      className={
        loading
          ? "email-settings add-products-wrap loader"
          : "email-settings add-products-wrap"
      }
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          padding: "0 20px",
        }}
      >
        <Box display="flex" flexDirection="column" justifyContent="end">
          <Typography
            sx={{
              borderBottom: "2px solid #0157FF",
              color: "#0157FF",
              fontFamily: "Poppins",
              fontSize: "14px",
              padding: "12px 0",
              lineHeight: "24px",
              fontWeight: 500,
            }}
          >
            Configure Email
          </Typography>
        </Box>
        <Box>
          <Button
            type="button"
            variant="outlined"
            sx={{ textTransform: "uppercase" }}
            onClick={handleCancel}
            className="create-pro-btn "
            id="pro-cancel"
          >
            close
          </Button>

          <Button
            type="submit"
            variant="contained"
            sx={{ textTransform: "uppercase" }}
            onClick={handleSave}
            className="create-pro-btn guideTourCreateSave"
            id="pro-save"
          >
            Save
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          padding: "30px",
          overflowX: "auto",
        }}
      >
        <EmailSettingsHeader product={product} />

        <Tabs value={activeTab} onChange={handleTabChange} aria-label="tabs">
          {isFeedbackEnabled && <Tab label="Feedback"></Tab>}
          {isTicketEnabled && <Tab label="Ticket"></Tab>}
          {isFeatureRequestEnabled && <Tab label="Feature Request"></Tab>}
        </Tabs>

        {isFeedbackEnabled && activeTabLabel === "Feedback" && (
          <Feedback
            aiSettings={feedbackAISettings}
            setAISettings={setFeedbackAISettings}
            accordionSettings={feedbackAccordionSettings}
            handleAccordionChange={handleFeedbackAccordionChange}
            kbArticles={kbArticles}
            connectionDetails={feedbackConnectionDetails}
            setConnectionDetails={setFeedbackConnectionDetails}
            isAutoAssign={isAutoAssign}
            handleIsAutoAssignChange={handleIsAutoAssignChange}
            emailSettings={feedbackEmailSettings}
            setEmailSettings={setFeedbackEmailSettings}
          />
        )}
        {isTicketEnabled && activeTabLabel === "Ticket" && (
          <Ticket
            bugSeverities={bugSeverities}
            aiSettings={ticketAISettings}
            setAISettings={setTicketAISettings}
            accordionSettings={ticketAccordionSettings}
            handleAccordionChange={handleTicketAccordionChange}
            kbArticles={kbArticles}
            connectionDetails={ticketConnectionDetails}
            setConnectionDetails={setTicketConnectionDetails}
            isAutoAssign={isAutoAssign}
            handleIsAutoAssignChange={handleIsAutoAssignChange}
            emailSettings={ticketEmailSettings}
            setEmailSettings={setTicketEmailSettings}
          />
        )}
        {isFeatureRequestEnabled && activeTabLabel === "Feature Request" && (
          <FeatureRequest
            aiSettings={featureRequestAISettings}
            setAISettings={setFeatureRequestAISettings}
            accordionSettings={featureRequestAccordionSettings}
            handleAccordionChange={handleFeatureRequestAccordionChange}
            kbArticles={kbArticles}
            connectionDetails={featureRequestConnectionDetails}
            setConnectionDetails={setFeatureRequestConnectionDetails}
            isAutoAssign={isAutoAssign}
            handleIsAutoAssignChange={handleIsAutoAssignChange}
            emailSettings={featureRequestEmailSettings}
            setEmailSettings={setFeatureRequestEmailSettings}
          />
        )}
      </Box>
    </div>
  );
};

export default React.memo(withSnackbar(EmailSettings));
